import { useState } from "react";
import Footer from "../../Components/Footer/Footer";
import siteConfig from "../../config/site";

const API_URL = siteConfig.apiUrl;

const Hiring = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    availability: "",
    intro: "",
    interest: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    try {
      const res = await fetch(`${API_URL}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError("Unable to submit. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClasses =
    "w-full bg-gray-900/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 transition-all";
  const labelClasses = "block text-gray-300 text-sm font-medium mb-2";

  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      {/* Header */}
      <div className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            Join Our Team
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-6" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We're building something different. If you have stage presence, professionalism, 
            and the drive to perform — we want to hear from you.
          </p>
        </div>
      </div>

      {/* What We Offer */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
            What We Offer
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "🎭", title: "Persona Development Training", desc: "We help you develop a compelling stage persona with depth and narrative." },
              { icon: "📋", title: "Booking Infrastructure", desc: "We handle the marketing, booking, scheduling, and client communication." },
              { icon: "🛡️", title: "Safety Framework", desc: "Comprehensive safety protocols, check-ins, and vetted clients. Your safety comes first." },
              { icon: "⏰", title: "Flexible Schedule", desc: "Work when you want. Set your own availability and take the bookings that work for you." },
              { icon: "💰", title: "Weekly Pay", desc: "Get paid weekly. Fair rates, transparent splits, no hidden fees or predatory commissions." },
              { icon: "🌟", title: "Professional Growth", desc: "Training, feedback, and a team that invests in your development as a performer." },
            ].map((item, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30 hover:border-purple-900/40 transition-all duration-300">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
            What We Look For
          </h2>
          <div className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/30">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="text-white font-bold mb-2">Reliability</h3>
                <p className="text-gray-400 text-sm">Show up on time, communicate clearly, follow through on commitments.</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">💃</div>
                <h3 className="text-white font-bold mb-2">Stage Presence</h3>
                <p className="text-gray-400 text-sm">You know how to command a room. Confidence, energy, and the ability to connect.</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">👔</div>
                <h3 className="text-white font-bold mb-2">Professionalism</h3>
                <p className="text-gray-400 text-sm">This is a business. We treat it like one. Boundaries, respect, and quality.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="px-6 pb-20">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
            Apply Now
          </h2>

          {submitted ? (
            <div className="p-8 rounded-2xl bg-gray-900/40 border border-green-800/30 text-center">
              <div className="text-5xl mb-4">✨</div>
              <h3 className="text-2xl font-bold text-white mb-3">Application Received</h3>
              <p className="text-gray-400">
                Thank you for your interest. We'll review your application and be in touch soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={labelClasses}>Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full name or stage name"
                  required
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(optional)"
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>Experience</label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="Tell us about your performance experience — dance, stage, entertainment, etc. No experience required."
                  rows="3"
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>Availability</label>
                <input
                  type="text"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  placeholder="e.g., Weekends, Evenings, Flexible"
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>Short Introduction *</label>
                <textarea
                  name="intro"
                  value={formData.intro}
                  onChange={handleChange}
                  placeholder="A few sentences about who you are and what you bring."
                  rows="3"
                  required
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={labelClasses}>What Interests You About Exo?</label>
                <textarea
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  placeholder="What caught your eye? What are you looking for?"
                  rows="3"
                  className={inputClasses}
                />
              </div>

              {submitError && (
                <div className="p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-400 text-sm">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-gold text-black font-bold text-lg rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>

              <p className="text-gray-500 text-sm text-center">
                All applications are confidential. We respect your privacy.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Hiring;
